<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useBooking } from '@/composables/useBooking';
import { GM_IMAGES, u } from '@/data/images';

const { openBooking } = useBooking();

const HERO_SLIDES = [
  { key: 'Serengeti', region: 'Northern Circuit', img: GM_IMAGES.lion, meta: 'Great Migration · Big Cats' },
  { key: 'Ngorongoro', region: 'Crater Highlands', img: GM_IMAGES.elesPink, meta: 'World Heritage Crater' },
  { key: 'Zanzibar', region: 'Spice Islands', img: GM_IMAGES.zanzibar, meta: 'White Sand · Turquoise Sea' },
  { key: 'Kilimanjaro', region: 'Northern Tanzania', img: GM_IMAGES.kilimanjaro, meta: "Africa's Rooftop · 5,895m" },
  { key: 'Tarangire', region: 'Manyara Region', img: GM_IMAGES.giraffeSunset, meta: 'Giant Baobabs · Elephants' },
];

const n = HERO_SLIDES.length;
const active = ref(0);
const heroRef = ref(null);
let timer = null;

// Only download a hero background once it's about to be shown. Start with the
// first slide (and the next, ready to crossfade); reveal the rest as they come
// up so the page doesn't fetch five full-bleed images at once.
const loaded = ref(HERO_SLIDES.map((_, i) => i <= 1 % n));
watch(active, (i) => {
  loaded.value[i] = true;
  loaded.value[(i + 1) % n] = true;
});

const go = (dir) => { active.value = (active.value + dir + n) % n; };
const jump = (i) => { active.value = i; };

const startAuto = () => {
  clearInterval(timer);
  timer = setInterval(() => { active.value = (active.value + 1) % n; }, 5200);
};
const restart = () => startAuto();

const onMove = (e) => {
  const el = heroRef.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width - 0.5;
  const y = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty('--px', x.toFixed(3));
  el.style.setProperty('--py', y.toFixed(3));
};

onMounted(() => {
  startAuto();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduce && heroRef.value) heroRef.value.addEventListener('mousemove', onMove);
});
onUnmounted(() => {
  clearInterval(timer);
  if (heroRef.value) heroRef.value.removeEventListener('mousemove', onMove);
});

// 3D transform for each deck card based on offset from the active slide.
const cardStyle = (i) => {
  let off = (i - active.value + n) % n;
  if (off > n / 2) off -= n;
  const abs = Math.abs(off);
  const sign = Math.sign(off) || 1;
  if (off === 0) {
    return { transform: 'translateX(-30px) translateZ(0) rotateY(-6deg) scale(1)', opacity: 1, zIndex: 50, filter: 'none' };
  }
  const x = 150 + abs * 86;
  const z = -120 * abs;
  const rot = -10 - abs * 2;
  const scale = 1 - abs * 0.08;
  const shown = off > 0 && abs <= 3;
  return {
    transform: `translateX(${sign > 0 ? x : -x - 120}px) translateZ(${z}px) rotateY(${rot}deg) scale(${scale})`,
    opacity: shown ? 1 - abs * 0.14 : 0,
    zIndex: 40 - abs,
    filter: `brightness(${1 - abs * 0.12})`,
    pointerEvents: off === 1 ? 'auto' : 'none',
  };
};
</script>

<template>
  <section class="hero" id="top" ref="heroRef" :style="{ '--px': 0, '--py': 0 }">
    <div
      class="hero__bgs"
      style="transform: translate(calc(var(--px) * -22px), calc(var(--py) * -16px)); position: absolute; inset: -30px;"
    >
      <div
        v-for="(s, i) in HERO_SLIDES"
        :key="s.key"
        :class="['hero__bg', { 'is-active': i === active }]"
        :style="{ backgroundImage: loaded[i] ? `url(${u(s.img, 1600)})` : 'none' }"
        :aria-hidden="i !== active"
      />
    </div>
    <div class="hero__scrim" />
    <div class="hero__scrim2" />

    <div class="wrap wrap--xl">
      <div class="hero__grid">
        <div class="hero__content">
          <span class="eyebrow eyebrow--light">Tanzania · Licensed Tour Operator</span>
          <h1 class="hero__title">Discover Tanzania <em>Beyond Imagination</em></h1>
          <p class="hero__sub">
            Experience unforgettable safaris, cultural journeys, mountain adventures and
            breathtaking beaches with Good Morning Tours &amp; Safari.
          </p>
          <div class="hero__cta">
            <button class="gm-btn gm-btn--primary gm-btn--lg" @click="openBooking()">Book Your Safari</button>
            <a class="gm-btn gm-btn--glass gm-btn--lg" href="#destinations">Explore Destinations</a>
          </div>
          <div class="hero__trust">
            <div class="hero__trust-item">
              <span style="color: var(--sand-400); font-size: 18px; letter-spacing: 2px;">★★★★★</span>
              <span class="t-lab">5-Star<br />Service</span>
            </div>
            <span class="hero__trust-sep" />
            <div class="hero__trust-item">
              <span class="t-num">2026</span>
              <span class="t-lab">Founded<br />in Arusha</span>
            </div>
            <span class="hero__trust-sep" />
            <div class="hero__trust-item">
              <span class="t-num">100%</span>
              <span class="t-lab">Private &amp;<br />Tailor-Made</span>
            </div>
          </div>
        </div>

        <div class="deck" style="transform: translate(calc(var(--px) * 18px), calc(var(--py) * 12px));">
          <div
            v-for="(s, i) in HERO_SLIDES"
            :key="s.key"
            class="deck__card"
            :style="cardStyle(i)"
            @click="jump(i); restart();"
          >
            <img :src="u(s.img, 700)" :alt="s.key" />
            <div class="deck__cap">
              <div class="k">{{ s.region }}</div>
              <div class="n">{{ s.key }}</div>
              <div class="m">{{ s.meta }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="hero__dots">
      <button
        v-for="(s, i) in HERO_SLIDES"
        :key="s.key"
        :class="['hero__dot', { 'is-active': i === active }]"
        :aria-label="'Go to ' + s.key"
        @click="jump(i); restart();"
      />
    </div>
    <div class="hero__arrows">
      <button class="hero__arrow" aria-label="Previous" @click="go(-1); restart();">‹</button>
      <button class="hero__arrow" aria-label="Next" @click="go(1); restart();">›</button>
    </div>
  </section>
</template>
